import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  SimpleGrid,
  Checkbox,
  Divider,
  Heading,
  Badge,
  VStack,
  HStack,
  Flex,
  Tag,
  useToast,
  Spinner
} from '@chakra-ui/react';

/**
 * Modal to display and select extracted information from AI
 */
const ExtractInfoModal = ({ isOpen, onClose, leadData, extractedData, onUpdateLead }) => {
  const [selectedFields, setSelectedFields] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  // Validate extracted data for a field
  const isValidData = (field, value) => {
    if (value === null || value === undefined) return false;
    
    // Check for empty arrays
    if (Array.isArray(value) && value.length === 0) return false;
    
    // Check for empty objects
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    
    // Specific validation for budget
    if (field === 'budget' && (!value.amount || isNaN(value.amount) || value.amount <= 0)) return false;
    
    // Specific validation for eventDates
    if (field === 'eventDates' && Array.isArray(value)) {
      // Check if all dates are valid
      return value.every(date => {
        try {
          const parsed = new Date(date);
          return !isNaN(parsed.getTime()); // Valid date if not NaN
        } catch (e) {
          return false;
        }
      });
    }
    
    // Specific validation for events
    if (field === 'events' && Array.isArray(value)) {
      // Valid if at least one event has a type
      return value.some(event => !!event.type);
    }
    
    // Specific validation for locations
    if (field === 'locations' && typeof value === 'object') {
      // Valid if it has at least one city or venue
      const hasCities = Array.isArray(value.cities) && value.cities.length > 0;
      const hasVenues = Array.isArray(value.venues) && value.venues.length > 0;
      return hasCities || hasVenues;
    }
    
    // Specific validation for roomCount and guestCapacity
    if ((field === 'roomCount' || field === 'guestCapacity') && (isNaN(value) || value <= 0)) return false;
    
    // For other fields, assume valid if non-empty
    return true;
  };
  
  // Reset selected fields when new data is provided
  useEffect(() => {
    if (extractedData) {
      // By default, select all fields that have valid values
      const initialSelection = {};
      Object.keys(extractedData).forEach(key => {
        // Only pre-select valid values
        if (isValidData(key, extractedData[key])) {
          initialSelection[key] = true;
        } else {
          console.log(`[AI] Auto-unselected invalid field: ${key}`, extractedData[key]);
          initialSelection[key] = false;
        }
      });
      setSelectedFields(initialSelection);
    }
  }, [extractedData]);

  const handleCheckboxChange = (field) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    Object.keys(extractedData).forEach(key => {
      // Only select non-empty values
      if (
        extractedData[key] !== null && 
        extractedData[key] !== undefined && 
        (typeof extractedData[key] !== 'object' || 
         Object.keys(extractedData[key]).length > 0)
      ) {
        allSelected[key] = true;
      }
    });
    setSelectedFields(allSelected);
  };

  const handleDeselectAll = () => {
    setSelectedFields({});
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      
      // Build update payload with only selected fields
      const updatePayload = {};
      Object.keys(selectedFields).forEach(field => {
        if (selectedFields[field] && extractedData[field] !== undefined) {
          updatePayload[field] = extractedData[field];
        }
      });
      
      // Call the parent function to update the lead
      await onUpdateLead(updatePayload);
      
      toast({
        title: 'Lead updated',
        description: 'Lead information has been updated with AI extracted data',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onClose();
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update lead with extracted data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Wrapper function that passes the field name to renderValue
  const renderValueWithField = (value, fieldName) => {
    try {
      // Wrap in try-catch to handle any rendering errors
      return renderValue(value, fieldName);
    } catch (error) {
      console.error(`[AI-Extract] Error rendering field ${fieldName}:`, error, value);
      return <Text color="red.500" fontSize="sm">[Rendering Error]</Text>;
    }
  };
  
  // Render different types of data (arrays, objects, strings, etc.)
  const renderValue = (value, field) => {
    // Safely convert any complex objects to prevent React rendering errors
    const safeRender = (val) => {
      if (val === undefined || val === null) {
        return <Text as="i" color="gray.400">Not set</Text>;
      }
      
      // Handle objects with _id and name (like stage, leadSource)
      if (typeof val === 'object' && !Array.isArray(val) && val._id && val.name) {
        return val.name;
      }
      
      // If it's an object that shouldn't be rendered directly
      if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length > 0) {
        try {
          // Try to extract a meaningful representation
          if (val.name) return val.name;
          if (val.title) return val.title;
          if (val.label) return val.label;
          if (val.value) return safeRender(val.value); // Recursively handle nested value property
          
          // Otherwise convert to JSON string for debugging but not in production
          return JSON.stringify(val);
        } catch (e) {
          console.error('Error safely rendering object:', e);
          return '[Complex Object]';
        }
      }
      
      // For primitive values, convert to string
      return String(val);
    };
    
    // If value is undefined or null
    if (value === undefined || value === null) {
      return <Text as="i" color="gray.400">Not set</Text>;
    }
    
    // If it's a boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // If it's a date
    if (field === 'eventDates' || field === 'nextFollowUpTime' || field === 'dateAdded' || 
        (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/))) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      } catch (e) {
        // Fall back to default rendering
      }
    }
    
    // If it's a tag array
    if (field === 'tags' && Array.isArray(value)) {
      if (value.length === 0) {
        return <Text as="i" color="gray.400">No tags</Text>;
      }
      return (
        <Flex wrap="wrap" gap={1}>
          {value.map((tag, idx) => (
            <Badge key={idx} colorScheme="blue" variant="subtle">
              {typeof tag === 'object' ? safeRender(tag) : tag}
            </Badge>
          ))}
        </Flex>
      );
    }
    
    // If it's an array of events
    if (field === 'events' && Array.isArray(value)) {
      if (value.length === 0) {
        return <Text as="i" color="gray.400">No events</Text>;
      }
      return (
        <VStack align="start" spacing={1}>
          {value.map((event, idx) => (
            <HStack key={idx}>
              <Badge colorScheme="purple">{typeof event.type === 'object' ? safeRender(event.type) : event.type}</Badge>
              {event.date && <Text fontSize="xs">{new Date(event.date).toLocaleDateString()}</Text>}
            </HStack>
          ))}
        </VStack>
      );
    }
    
    // If it's an array
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <Text as="i" color="gray.400">Empty list</Text>;
      }
      
      // If it's an array of strings or numbers
      if (value.every(item => typeof item === 'string' || typeof item === 'number')) {
        return (
          <Flex wrap="wrap" gap={1}>
            {value.map((item, idx) => (
              <Tag key={idx} size="sm" variant="subtle" colorScheme="gray">
                {item}
              </Tag>
            ))}
          </Flex>
        );
      }
      
      // If it's an array of objects
      return (
        <VStack align="start" spacing={1} w="100%">
          {value.map((item, idx) => (
            <Box key={idx} p={1} borderWidth="1px" borderRadius="sm" w="100%">
              {typeof item === 'object' ? (
                Object.entries(item).map(([key, val]) => (
                  <Box key={key}>
                    <Text as="span" fontWeight="bold">{key}:</Text>{' '}
                    <Text as="span">
                      {val === null || val === undefined ? 'Not set' : safeRender(val)}
                    </Text>
                  </Box>
                ))
              ) : (
                <Text>{String(item)}</Text>
              )}
            </Box>
          ))}
        </VStack>
      );
    }
    
    // Handle objects (like budget, locations)
    if (typeof value === 'object') {
      // If it's the budget object
      if (field === 'budget' && typeof value === 'object') {
        return (
          <Box>
            {value.amount && (
              <Text>{value.amount.toLocaleString()} {value.currency || 'INR'}</Text>
            )}
            {!value.amount && value.currency && (
              <Text>Currency: {value.currency}</Text>
            )}
            {value.notes && <Text fontSize="xs" mt={1}>{value.notes}</Text>}
          </Box>
        );
      }
      
      // If it's a locations object
      if (field === 'locations' && typeof value === 'object') {
        return (
          <VStack align="start" spacing={1}>
            {value.cities && value.cities.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xs">Cities:</Text>
                <Flex wrap="wrap" gap={1}>
                  {value.cities.map((city, idx) => (
                    <Badge key={idx} colorScheme="blue" variant="outline">
                      {typeof city === 'object' ? JSON.stringify(city) : String(city)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}
            {value.venues && value.venues.length > 0 && (
              <Box mt={1}>
                <Text fontWeight="bold" fontSize="xs">Venues:</Text>
                <Flex wrap="wrap" gap={1}>
                  {value.venues.map((venue, idx) => (
                    <Badge key={idx} colorScheme="green" variant="outline">
                      {typeof venue === 'object' ? JSON.stringify(venue) : String(venue)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}
          </VStack>
        );
      }
      
      // Special case for stage and leadSource objects (common problematic fields)
      if ((field === 'stage' || field === 'leadSource') && typeof value === 'object' && value.name) {
        return <Box fontSize="sm">{value.name}</Box>;
      }
      
      // If it's another kind of object
      if (typeof value === 'object' && !Array.isArray(value)) {
        return (
          <VStack align="start" spacing={1}>
            {Object.entries(value).map(([subKey, subVal]) => {
              return (
                <Box key={subKey} w="100%">
                  <Text as="span" fontWeight="bold">{subKey}:</Text>{' '}
                  <Text as="span">
                    {subVal === null || subVal === undefined ? 'Not set' : safeRender(subVal)}
                  </Text>
                </Box>
              );
            })}
          </VStack>
        );
      }
    }
    
    // Handle simple values with safe conversion
    return <Box fontSize="sm">{String(value)}</Box>;
  };

  if (!extractedData) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxWidth="1000px">
        <ModalHeader>
          <Flex justify="space-between" align="center">
            <Text>AI Extracted Lead Information</Text>
            <HStack>
              <Button size="xs" colorScheme="blue" variant="outline" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button size="xs" colorScheme="gray" variant="outline" onClick={handleDeselectAll}>
                Deselect All
              </Button>
            </HStack>
          </Flex>
        </ModalHeader>
        <Divider />
        
        <ModalBody py={4}>
          <Text mb={4} fontSize="sm" color="gray.600">
            AI has extracted the following information. Select the fields you want to update.
          </Text>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {Object.keys(extractedData).map(field => {
              // Skip undefined or empty values
              if (
                extractedData[field] === undefined || 
                extractedData[field] === null || 
                (typeof extractedData[field] === 'object' && 
                 !Array.isArray(extractedData[field]) && 
                 Object.keys(extractedData[field]).length === 0)
              ) {
                return null;
              }
              
              return (
                <Box 
                  key={field} 
                  p={3} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  bg={selectedFields[field] ? "blue.50" : "white"}
                >
                  <Checkbox 
                    isChecked={selectedFields[field] || false}
                    onChange={() => handleCheckboxChange(field)}
                    colorScheme="blue"
                    mb={2}
                  >
                    <Text fontWeight="bold" textTransform="capitalize">
                      {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Text>
                  </Checkbox>
                  
                  <SimpleGrid columns={2} spacing={2} mt={2}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Current Value:</Text>
                      <Box fontSize="sm" mt={1}>
                        {renderValueWithField(leadData?.[field], field)}
                      </Box>
                    </Box>
                    
                    <Box>
                      <Text fontSize="xs" color="gray.500">AI Extracted:</Text>
                      <Box fontSize="sm" mt={1} fontWeight="medium">
                        {renderValueWithField(extractedData[field], field)}
                      </Box>
                    </Box>
                  </SimpleGrid>
                </Box>
              );
            })}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Discard
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleUpdate}
            isLoading={isUpdating}
            loadingText="Updating"
            isDisabled={Object.keys(selectedFields).length === 0}
          >
            Update Lead
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ExtractInfoModal;
